import { AnalyticEvent, match, usePostHog, useV2GetBundleBySlugQuery } from "@stepapp/core";
import { AttributeType, V2CartQuestionsUpsert, V2CartServicesUpsert } from "@stepapp/core/apiTypes";
import { Block, Input, TouchableInput, Typography } from "@stepapp/ui";
import { useGlobalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { ScrollView } from "react-native-gesture-handler";
import {
  HouseAreaOutOfScopeSheet,
  QuantityAnswerItem,
  ServiceSceneDescription,
  ServiceSceneHeader,
  SimpleAnswerItem,
} from "../../../src/components";
import CategoryQuestionAnswersSheet from "../../../src/components/Service/CategoryQuestionAnswersSheet";
import CategoryQuestionItem from "../../../src/components/Service/CategoryQuestionItem";
import IncludedInPriceItem from "../../../src/components/Service/IncludedInPriceItem";
import { useAppDispatch, useAppSelector } from "../../../src/hooks";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";
import { setHomeSize } from "../../../src/store/homeProfile";

const ServiceQuestionScreen = () => {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const properties = useAppSelector(({ user }) => user.properties);
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const { slug = "", ...rest } = useGlobalSearchParams<{ slug: string; [key: string]: string }>();
  const { navigate } = useRouter();
  const appDispatch = useAppDispatch();
  const [modalVisible, setModalVisible] = useState({
    isVisible: false,
    categoryId: "",
    questionId: "",
  });

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();

  const filteredProperties = useMemo(
    () => properties.filter((item) => item?.localizationId === localizationId),
    [properties, localizationId],
  );

  const hasProperties = !!filteredProperties?.[0]?.propertyId;
  const questionId = rest["service-question"]?.split("question_")[1];

  const [address, houseArea, subscriptionOptionId, attributes, servicesValues] = watch([
    "address",
    "houseArea",
    "subscriptionOptionId",
    "attributes",
    "services",
  ]);

  const pickedAddress = useMemo(
    () => (address?.streetAddress1 ? `${address?.streetAddress1}, ${address?.city}` : ""),
    [address?.streetAddress1, address?.city],
  );

  const { data } = useV2GetBundleBySlugQuery({ variables: { localizationId, slug } });
  const bundle = useMemo(() => data?.v2GetBundleBySlug, [data?.v2GetBundleBySlug]);

  const subscriptionQuestion = useMemo(() => {
    if (bundle?.subscriptionOptions?.length === 0) return;

    return {
      id: "subscription",
      position: -1,
      type: AttributeType.SingleChoice,
      numericType: null,
      name: t("screens.service.howOften"),
      isRequired: true,
      answers: bundle?.subscriptionOptions
        ?.concat()
        .sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured))
        .map(({ id, title, discount, description, isFeatured }) => ({
          id,
          name: title,
          isFeatured,
          price: 0,
          position: -1,
          description,
          discount,
        })),
    };
  }, [bundle, t]);

  const attributeQuestions = useMemo(() => {
    const attributes = bundle?.questions?.length === 0 ? [] : bundle?.questions || [];
    const categories =
      bundle?.categories?.length === 0
        ? []
        : bundle?.categories.map(({ title, ...rest }) => ({
            name: title,
            isRequired: false,
            ...rest,
          })) || [];

    return [...categories, ...attributes];
  }, [bundle?.questions, bundle?.categories]);

  const serviceQuestions = useMemo(() => {
    if (bundle?.services?.length === 0) return [];

    const servicesToOrder = bundle?.services?.filter(
      (service) => !service?.variants?.some((variant) => variant?.isFree),
    );
    const servicesIncludedInPrice = bundle?.services?.filter((service) =>
      service?.variants?.some((variant) => variant?.isFree),
    );

    const showCustomTitleAndDescription = bundle?.isAdditionalTitleAndDescriptionEnabled;
    const name = showCustomTitleAndDescription
      ? bundle?.additionalTitle
      : t("screens.service.additionalServices");
    const description = showCustomTitleAndDescription
      ? bundle?.additionalDescription
      : t("screens.service.additionalServicesDescription");

    return [
      {
        position: 999,
        id: "services",
        name,
        description,
        isRequired: false,
        services: {
          servicesToOrder,
          servicesIncludedInPrice,
        },
      },
    ];
  }, [bundle, t]);

  const bundleOptions = useMemo(() => {
    if (subscriptionQuestion && serviceQuestions.length > 0) {
      return [subscriptionQuestion, ...attributeQuestions, ...serviceQuestions];
    }
    if (subscriptionQuestion) {
      return [subscriptionQuestion, ...attributeQuestions];
    }
    if (serviceQuestions.length > 0) {
      return [...attributeQuestions, ...serviceQuestions];
    }

    return attributeQuestions;
  }, [attributeQuestions, serviceQuestions, subscriptionQuestion]);

  const questions = useMemo(
    () => bundleOptions.sort((a, b) => a.position - b.position),
    [bundleOptions],
  );

  const currentQuestion = useMemo(
    () =>
      questions.find((item) => item?.id === questionId) ||
      questions.find(
        (item) =>
          "questions" in item && item?.questions?.map((question) => question?.id === questionId),
      ),
    [questions, questionId],
  );

  const showAddressInput = useMemo(
    () => questions[0]?.id === questionId && !hasProperties,
    [questions, questionId, hasProperties],
  );

  const showHomeSizeInput =
    (questionId === "subscription" && !!bundle?.houseArea?.enabled && !hasProperties) ||
    (data?.v2GetBundleBySlug?.subscriptionOptions?.length === 0 &&
      questionId === attributeQuestions[0]?.id &&
      !!bundle?.houseArea?.enabled &&
      !hasProperties);

  const homeSizeValidation = useCallback(() => {
    const size = houseArea || 0;

    appDispatch(setHomeSize(size));
    postHog?.capture(AnalyticEvent.User.HomeSize, { size });
    trigger("houseArea");
  }, [appDispatch, houseArea, postHog, trigger]);

  const handleSetHouseArea = useCallback(
    (value: string) => {
      const size = Number.parseInt(value) || 0;
      setValue("houseArea", size);
      setValue("address.floorArea", size);

      if (size >= 10) {
        trigger("houseArea");
      }
    },
    [setValue, trigger],
  );

  const setNewServices = useCallback(
    (services: V2CartServicesUpsert[]) => {
      setValue("services", services, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const setNewAttribute = useCallback(
    (attributes: V2CartQuestionsUpsert[]) => {
      setValue("attributes", attributes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const setSubscriptionId = useCallback(
    (id: string) => {
      setValue("subscriptionOptionId", id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const handleCloseModal = useCallback(() => {
    setModalVisible({
      isVisible: false,
      categoryId: "",
      questionId: "",
    });
  }, []);

  const renderQuestion = useCallback(() => {
    if (!currentQuestion) return null;
    const { id: questionId } = currentQuestion;

    return (
      <Block marginBottom={14}>
        {match(currentQuestion)
          .when(
            (q) => q && "questions" in q,
            ({ questions }) => (
              <>
                {questions
                  ?.concat()
                  .sort((a, b) => a.position - b.position)
                  .map(({ id, name, description, isFeatured, type, answers, numericType }) => (
                    <CategoryQuestionItem
                      answers={answers}
                      questionType={type}
                      description={description}
                      isFeatured={isFeatured}
                      key={id}
                      questionId={id}
                      categoryId={questionId}
                      name={name}
                      attributes={attributes}
                      numericType={numericType}
                      setModalVisible={setModalVisible}
                    />
                  ))}
              </>
            ),
          )
          .when(
            (q) => q && "answers" in q,
            ({ answers, type, numericType }) => (
              <>
                {answers
                  ?.concat()
                  .sort((a, b) => a.position - b.position)
                  .map(({ id, name, description, isFeatured = false, ...rest }) => {
                    const discount = ("discount" in rest && rest?.discount) || 0;
                    const isSelected =
                      questionId === "subscription"
                        ? id === subscriptionOptionId
                        : (attributes?.some((a) => a?.valueId?.includes(id)) ?? false);

                    return (
                      <SimpleAnswerItem
                        key={id}
                        id={id}
                        title={name}
                        description={description}
                        discount={discount}
                        isFeatured={isFeatured}
                        questionType={type}
                        questionId={questionId}
                        isPeriodicityQuestion={questionId === "subscription"}
                        setNewAttribute={setNewAttribute}
                        setSubscriptionId={setSubscriptionId}
                        selected={isSelected}
                        attributes={attributes}
                        numericType={numericType}
                      />
                    );
                  })}
              </>
            ),
          )
          .when(
            (q) => q && "services" in q,
            ({ services }) => (
              <>
                {services?.servicesIncludedInPrice?.map(({ icon, id, name, description }) => (
                  <IncludedInPriceItem key={id} icon={icon} name={name} description={description} />
                ))}
                {services?.servicesToOrder?.map(({ description, icon, id, name, variants }) => (
                  <QuantityAnswerItem
                    key={id}
                    id={id}
                    name={name}
                    description={description}
                    icon={icon}
                    variants={variants}
                    setNewServices={setNewServices}
                    services={servicesValues}
                  />
                ))}
              </>
            ),
          )
          .otherwise(() => null)}
      </Block>
    );
  }, [
    attributes,
    currentQuestion,
    setNewServices,
    setNewAttribute,
    setSubscriptionId,
    servicesValues,
    subscriptionOptionId,
  ]);

  useEffect(() => {
    if (showAddressInput && address?.streetAddress1) {
      setValue("address.streetAddress1", address?.streetAddress1 || "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [setValue, showAddressInput, address?.streetAddress1]);

  const selectedQuestion =
    bundle?.categories
      ?.find((cat) => cat?.id === modalVisible?.categoryId)
      ?.questions?.find((question) => question?.id === modalVisible?.questionId) || undefined;

  if (!currentQuestion) return null;

  return (
    <ScrollView>
      {showHomeSizeInput && (
        <Block paddingTop={2}>
          <Typography variant="newH2">{bundle?.name}</Typography>

          <Block paddingTop={1} paddingBottom={3}>
            <Typography type="secondary" variant="paragraph">
              {t("screens.service.subscriptionExplanationForEdit")}
            </Typography>
          </Block>

          <Input
            required
            error={errors?.houseArea?.message}
            keyboardType="number-pad"
            onBlur={homeSizeValidation}
            onChangeText={handleSetHouseArea}
            placeholder={t("components.input.placeholder.size")}
            suffixComponent={() => (
              <Typography variant="bodyBold">{t("screens.flatSize.unit")}</Typography>
            )}
            value={(houseArea || 0) > 0 ? `${houseArea}` : ""}
          />
        </Block>
      )}

      {showAddressInput && (
        <Block paddingTop={2}>
          <TouchableInput
            required
            onPress={() => navigate(`/service/${slug}/address-autocomplete`)}
            placeholder={t("components.input.placeholder.typeAddress")}
            value={pickedAddress}
          />

          <Block paddingBottom={1}>
            <Typography type="secondary" variant="lightLabel">
              <Trans t={t} i18nKey="screens.service.autocompleteInfo">
                <Typography type="secondary" variant="label">
                  Enter the full street name
                </Typography>{" "}
                to check the availability of our services. In the next steps, you can add additional
                information.
              </Trans>
            </Typography>
          </Block>
        </Block>
      )}

      <ServiceSceneHeader name={currentQuestion?.name} isRequired={!!currentQuestion?.isRequired} />

      {"description" in currentQuestion && !!currentQuestion?.description && (
        <ServiceSceneDescription marginBottom={1} description={currentQuestion?.description} />
      )}

      {renderQuestion()}

      <HouseAreaOutOfScopeSheet formError={errors?.houseArea} />

      <CategoryQuestionAnswersSheet
        categoryType={bundle?.categoryType}
        isVisible={modalVisible.isVisible}
        question={selectedQuestion}
        closeModal={handleCloseModal}
        attributes={attributes}
        setNewAttribute={setNewAttribute}
      />
    </ScrollView>
  );
};

export default ServiceQuestionScreen;
